import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createBreakpoint, useMedia } from 'react-use'
import { format, getUnixTime } from 'date-fns'
import { subscribe } from 'react-contextual'
import { get, map, sum, sumBy } from 'lodash-es'
import { stringUtils } from '../../../../../../utils'
import { AggregateTable, CurrencyText, Header, Label, NoContent } from '../../../..'
import { InvoicesProvider, SearchFilterProvider } from '../../../../../providers'
import styles from './ActiveInvoiceTable.module.scss'
import { find, map as rMap, pipe, prop, reverse, sortBy, pathOr } from 'ramda'
import { sortByProps } from 'utils/array'
import { useSelector } from 'react-redux'
import { reconApiSelectors } from '../../../../../../modules/api/recon'
import { $TSFixMe } from 'types/ts-migrate'
import SvgInvoiceFill from 'views/components/atoms/Icons/Doc/InvoiceFill'
import NameCell from '../../NameCell'
import { useBreakpoints } from 'views/hooks'

const statusColorMap = {
  Blue: '#056DBA',
  Green: '#128269',
  Yellow: '#F7B735',
  Amber: '#FF6D21',
  Red: '#FF1900',
}

const setBreakpoints = createBreakpoint({ xs: 320, sm: 640, md: 768, lg: 1024, xl: 1280, xxl: 1440 })

const ActiveInvoiceTable = ({
  title = 'Active Invoices',
  activeInvoiceFilters,
  openInvoice,
  searchQuery,
  activeFilterIndex,
  animations,
  getInvoiceBeneficiariesForPaymentRequest,
  failedPaymentApprovals,
  getBankingValidationErrorsByPartyId,
  currentInvoice,
}: any): React.ReactElement => {
  const [data, setData] = useState<$TSFixMe[]>([])
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const filter = get(activeInvoiceFilters, `[${[activeFilterIndex]}]`, {
    invoices: [],
  })

  const getAmountAllocation = (inv: $TSFixMe) => {
    const getBeneficiariesBalanceSum = () => pipe(pathOr([], ['beneficiaries']), rMap(prop('balance')), sum)(inv)
    const getUnallocatedAmount = () => {
      const balance = pathOr(0, ['balance'], inv)
      return balance - getBeneficiariesBalanceSum()
    }
    const areAllFundsAllocated = (): boolean => getUnallocatedAmount() === 0

    if (filter.key === 'short') {
      return inv.customerAvailableFunds
    } else if (areAllFundsAllocated()) {
      return getBeneficiariesBalanceSum()
    } else {
      return getUnallocatedAmount()
    }
  }

  useEffect(() => {
    const defaultSortedData: $TSFixMe[] = pipe(
      rMap((inv: any) => ({
        invoiceTo: inv.customerName,
        dueDateSeconds: format(new Date(inv.dueDate), 't'),
        ...inv,
      })),
      (list: any) => sortByProps(['invoiceTo', 'dueDateSeconds'], list),
    )(filter.invoices)

    setData(
      defaultSortedData.map((invoice: $TSFixMe) => ({
        ...invoice,
        meta: {
          invoiceNumber: invoice.invoiceNumber.toString(),
          invoiceTo: invoice.customerName,
          property: invoice.property,
          type: invoice.invoiceType,
          dueDate: invoice.dueDate ? getUnixTime(new Date(invoice.dueDate)).toString() : '',
          amount: invoice.grossAmount.toString(),
          amountAllocation: getAmountAllocation(invoice).toString(),
        },
      })),
    )
  }, [filter.invoices])

  const _openInvoice = useCallback((id, type) => {
    openInvoice(id, type)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const status = get(filter, 'status', '')
  const filteredInvoices = useMemo(() => {
    return data.filter(inv => stringUtils.jsonStringSearch(searchQuery, inv))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  const getInvoiceParties = useSelector(reconApiSelectors.getInvoiceParties)

  const columns = [
    {
      Header: '',
      accessor: 'status',
      style: {
        maxWidth: '10px',
        padding: '0 15px',
        justifyContent: 'center',
        display: 'flex',
        alignItems: 'center',
      },
    },
    {
      Header: 'Invoice to',
      accessor: 'invoiceTo',
      style: {
        maxWidth: '200px',
      },
      sort: {
        enabled: true,
        order: false,
      },
    },
    {
      Header: 'Property Address',
      accessor: 'property',
      sort: {
        enabled: true,
        order: false,
      },
    },
    {
      Header: 'Invoice Type',
      accessor: 'type',
      style: {
        maxWidth: '150px',
      },
      sort: {
        enabled: true,
        order: false,
      },
    },
    {
      Header: 'Due date',
      accessor: 'dueDate',
      style: {
        maxWidth: '120px',
      },
      sort: {
        enabled: true,
        order: false,
      },
    },
    {
      Header: filter.key === 'short' ? 'Amount to Approve' : 'Invoice Amount',
      accessor: 'amount',
      style: {
        display: 'flex',
        justifyContent: 'flex-end',
        maxWidth: '200px',
      },
      sort: {
        enabled: true,
        order: false,
      },
    },
    {
      Header:
        filter.key === 'overdue'
          ? 'Amount Overdue'
          : filter.key === 'short'
          ? 'Available Funds'
          : filter.key === 'unallocated'
          ? 'Amount Unallocated'
          : 'Amount to Approve',
      accessor: 'amountAllocation',
      style: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: '20px',
        maxWidth: '200px',
      },
      sort: {
        enabled: true,
        order: false,
      },
    },
  ]

  const currentInvoiceId = currentInvoice?.id

  const breakpoint = setBreakpoints()
  const { isMobile } = useBreakpoints()

  const invoiceData: $TSFixMe = useMemo(() => {
    const buildInvoiceObj = (inv: any): any => {
      const hasBeneficiaries = getInvoiceBeneficiariesForPaymentRequest(inv.id).length > 0
      const bankingValidationErrors = getInvoiceParties(inv.id)
        .map(getBankingValidationErrorsByPartyId)
        .filter((err: any) => !!err)

      const hasPaymentFailed =
        !!failedPaymentApprovals.find(({ invoiceId }: any) => invoiceId === inv.id) ||
        bankingValidationErrors.length > 0

      const hasCreditNotes = inv.creditNotes.length > 0
      const amountMinusCreditNotes = hasCreditNotes
        ? inv.grossAmount - inv.creditNotes.reduce((acc: any, cn: any) => acc + cn.amount, 0)
        : inv.grossAmount

      const getBeneficiariesBalanceSum = () => pipe(pathOr([], ['beneficiaries']), rMap(prop('balance')), sum)(inv)

      const getUnallocatedAmount = () => {
        const balance = pathOr(0, ['balance'], inv)
        return balance - getBeneficiariesBalanceSum()
      }

      const areAllFundsAllocated = (): boolean => getUnallocatedAmount() === 0
      const propertyMainText =
        inv.metaData !== undefined && inv.metaData !== '' && inv.mainText !== undefined
          ? `${inv.metaData}, ${inv.mainText}`
          : inv.mainText !== undefined
          ? inv.mainText
          : ''

      return {
        status: (
          <Label circular empty size="sm" style={{ backgroundColor: statusColorMap[status] }}>
            &nbsp;
          </Label>
        ),
        id: inv.id,
        highlighted: currentInvoiceId === inv.id,
        meta: inv.meta, // used for sorting
        enableSelection: filter.key === 'ready' ? hasBeneficiaries : true,
        invoiceTo: inv.customerName,
        property: isMobile ? (
          <strong>{propertyMainText}</strong>
        ) : (
          <NameCell name={propertyMainText} meta={inv.secondaryText} />
        ),
        type: (
          <>
            <span className={styles['invoice-type-wrapper']}>
              <strong>{inv.invoiceType}</strong>
              <small>{`INV-${inv.invoiceNumber as string}`}</small>
            </span>
          </>
        ),
        dueDate: format(new Date(inv.dueDate), 'dd MMM yyyy'),
        amount:
          filter.key === 'short' && areAllFundsAllocated() ? (
            <CurrencyText>{getBeneficiariesBalanceSum()}</CurrencyText>
          ) : inv.grossAmount ? (
            <CurrencyText>{amountMinusCreditNotes}</CurrencyText>
          ) : (
            <em>No amount set</em>
          ),
        amountAllocation:
          filter.key === 'short' ? (
            <CurrencyText>{inv.customerAvailableFunds}</CurrencyText>
          ) : areAllFundsAllocated() ? (
            <CurrencyText>{getBeneficiariesBalanceSum()}</CurrencyText>
          ) : (
            <CurrencyText>{getUnallocatedAmount()}</CurrencyText>
          ),
        animateSuccess: !hasPaymentFailed && animations?.bulkApprovePayments.includes(inv.id),
        animateDangerFlash: animations?.bulkNudge.includes(inv.id),
        hasError: hasPaymentFailed,
      }
    }

    const tableData = searchQuery ? map(filteredInvoices, buildInvoiceObj) : map(data, buildInvoiceObj)

    return find(prop('hasError'), tableData)
      ? pipe(
          // @ts-expect-error
          sortBy(prop('hasError')),
          reverse,
        )(tableData)
      : tableData
  }, [
    data,
    filteredInvoices,
    currentInvoiceId,
    searchQuery,
    status,
    animations.bulkApprovePayments,
    animations.bulkNudge,
    filter.key,
    getInvoiceBeneficiariesForPaymentRequest,
    failedPaymentApprovals,
    getBankingValidationErrorsByPartyId,
    getInvoiceParties,
  ])

  const rowHeight = () => {
    switch (breakpoint) {
      case 'xs':
      case 'sm':
      case 'md':
        return 100
      case 'lg':
      case 'xl':
        return 130
      default:
        return 80
    }
  }

  return useMemo(
    () => (
      <div className={styles.root}>
        <Header icon={<SvgInvoiceFill />} text={title} />
        {invoiceData.length > 0 ? (
          <AggregateTable
            rowHeight={rowHeight()}
            enableSelection={!['unallocated', 'open'].includes(filter.key)}
            columns={columns}
            data={invoiceData}
            onRowClick={data => _openInvoice(data.id, 'active')}
          />
        ) : (
          <NoContent heading="All your invoices have been taken care of." />
        )}
      </div>
    ),
    [_openInvoice, columns, invoiceData, filter.key, title],
  )
}

export default subscribe(
  [InvoicesProvider, SearchFilterProvider],
  (
    {
      activeInvoiceFilters,
      openInvoice,
      animations,
      getInvoiceBeneficiariesForPaymentRequest,
      failedPaymentApprovals,
      getBankingValidationErrorsByPartyId,
      currentInvoice,
    }: any,
    { searchQuery, activeFilterIndex }: any,
  ) => ({
    // InvociesProvider props
    activeInvoiceFilters,
    openInvoice,
    animations,
    getInvoiceBeneficiariesForPaymentRequest,
    failedPaymentApprovals,
    getBankingValidationErrorsByPartyId,
    currentInvoice,
    // SearchFilterProvider props
    searchQuery,
    activeFilterIndex,
  }),
)(ActiveInvoiceTable)
